import { useSession } from "@pontimed/auth-client";
import { identify, reset } from "@pontimed/analytics/client";
import { useEffect, useRef } from "react";

/**
 * Mirrors the auth-client session into PostHog's distinct-id + person
 * properties. Port of legacy `posthog-identity-bridge.tsx` adapted to
 * `@pontimed/auth-client`'s `useSession` (Jotai-backed) — same effect contract,
 * different session API.
 *
 * Server-side `events.signIn` (apps/api) calls `identifyUser()` via
 * `posthog-node`, which creates the person profile and attaches non-PHI
 * properties (provider, language, premium, etc.). This bridge mirrors the
 * SAME identity to the browser SDK so client-side captures are attributed
 * to the right person — without it, anonymous client distinctIds would
 * produce two parallel profiles per user.
 *
 * Behaviour:
 *  - status==="authenticated" + new userId → identify
 *  - status==="authenticated" + same userId as previous render → no-op
 *  - status==="unauthenticated" after being logged in → reset
 *  - status==="loading" → no-op
 *
 * Properties: ONLY non-PHI fields. JWT claims surface role + scope; richer
 * person properties (locale, has_premium, etc.) come from the server-side
 * identifyUser() call in apps/api auth callbacks. Keep this list aligned
 * with the server payload to avoid desync.
 */
export function PostHogIdentityBridge() {
  const { user, status } = useSession();
  const lastIdentifiedRef = useRef<string | null>(null);

  useEffect(() => {
    if (status === "loading") return;

    const userId = user?.userId ?? null;
    const previousId = lastIdentifiedRef.current;

    if (status === "authenticated" && userId && userId !== previousId) {
      identify(userId, {
        // Non-PHI properties from the JWT session payload. Server-side
        // identifyUser() (apps/api auth callbacks) sets the richer
        // properties (locale, has_premium, premium_source, …); keep this
        // client mirror minimal to avoid drift.
        role: user?.role ?? null,
        is_superadmin: user?.isSuperadmin ?? false,
        has_workspace: !!user?.ws,
      });
      lastIdentifiedRef.current = userId;
      return;
    }

    if (status === "unauthenticated" && previousId !== null) {
      reset();
      lastIdentifiedRef.current = null;
    }
  }, [user, status]);

  return null;
}
